import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Building2, Store, Warehouse, Layers, AlertTriangle, Package, CheckCircle, XCircle } from "lucide-react";
import { fadeUp, Section, Card, GoldButton } from "../components/shared/UIComponents";
import { usePageNavigate } from "../hooks/usePageNavigate";

const ASSET_TYPES = [
  { icon: Building2, title: "Multifamily", primary: true, criteria: ["5 to 500+ units", "Value-add and workforce housing", "$1M to $50M deal size", "Any condition, any occupancy", "All markets — Texas priority"] },
  { icon: Store, title: "Retail", criteria: ["Strip centers", "Single tenant NNN", "Anchored retail", "Outparcels"] },
  { icon: Warehouse, title: "Industrial", criteria: ["Flex space", "Warehouse", "Light industrial", "Distribution"] },
  { icon: Layers, title: "Mixed-Use", criteria: ["Ground floor commercial", "Residential above", "Urban infill"] },
  { icon: AlertTriangle, title: "Distressed & Vacant", criteria: ["Underperforming assets", "Repositioning potential", "Turnaround plays"] },
  { icon: Package, title: "Portfolio Sales", criteria: ["Multiple properties", "Complex transactions", "Entity-level transfers"] },
];

const STRUCTURES = [
  { title: "Direct Purchase", desc: "We are the buyer. Fast close, no contingencies, all cash or structured financing. Our primary acquisition method." },
  { title: "Assignment", desc: "We contract the property and assign to our qualified buyer network. Ideal for sellers who need speed and certainty." },
  { title: "Joint Venture", desc: "We partner with sellers who want to remain involved in the upside. Structured equity partnerships." },
];

const GOOD_FIT = [
  "Motivated seller with clear timeline",
  "Flexible timeline or ASAP close needed",
  "Properties that need capital or management improvement",
  "Portfolio sales — multiple properties together",
  "Estate sales or partnership dissolutions",
  "Off-market or pre-market opportunities",
];

const NOT_FIT = [
  "Retail malls over $100M",
  "Raw land only (no improvements)",
  "Single-family residential homes",
  "Owner-occupied with no intent to sell",
];

export default function AcquisitionsPage() {
  const { navigateTo } = usePageNavigate();

  return (
    <>
      {/* Hero */}
      <section className="relative py-20 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold mb-4">Acquisitions</p>
            <h1 className="text-4xl font-bold text-cream sm:text-5xl md:text-6xl tracking-tight">We Buy <span className="luxury-text-gradient">Commercial Real Estate</span></h1>
            <p className="mt-5 max-w-2xl text-base text-cream/70 leading-relaxed sm:text-lg">
              Multifamily, retail, industrial, and mixed-use properties across the United States. Texas and Sun Belt focus.
            </p>
            <div className="mt-8">
              <GoldButton onClick={() => navigateTo("sell")} className="!px-6 !py-3 sm:!px-8 sm:!py-4 !text-xs sm:!text-sm">
                Submit Your Property <ArrowRight className="h-3.5 w-3.5 ml-1 inline" />
              </GoldButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Asset Types */}
      <Section eyebrow="What We Buy" title="Acquisition Criteria"
        description="We acquire commercial real estate assets nationally with primary focus in Texas and the Sun Belt.">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ASSET_TYPES.map((a, i) => (
            <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.06 }}>
              <Card className={`h-full !p-6 ${a.primary ? "!border-emerald-300 ring-1 ring-emerald-200" : ""}`}>
                {a.primary && <span className="text-[9px] font-bold uppercase tracking-[0.15em] text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full mb-3 inline-block">Primary Focus</span>}
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-100 to-emerald-50 border border-emerald-200 flex items-center justify-center mb-4">
                  <a.icon className="h-5 w-5 text-emerald-700" />
                </div>
                <h3 className="text-lg font-bold text-[#0A0A0A] mb-3">{a.title}</h3>
                <ul className="space-y-2">
                  {a.criteria.map((c, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-[#555]">
                      <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {c}
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* Deal Structures */}
      <section className="relative py-16 sm:py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950 via-[#022c22] to-emerald-950" />
        <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M20 0L40 20L20 40L0 20z' fill='none' stroke='%23E5C98B' stroke-width='0.5'/%3E%3C/svg%3E")`, backgroundSize: '40px 40px' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeUp} className="mb-10">
            <p className="text-xs sm:text-sm uppercase tracking-[0.4em] text-gold font-semibold">How We Structure</p>
            <h2 className="mt-3 text-3xl font-bold text-cream sm:text-4xl">Deal Structures</h2>
          </motion.div>
          <div className="grid gap-4 lg:grid-cols-3">
            {STRUCTURES.map((s, i) => (
              <motion.div key={i} {...fadeUp} transition={{ delay: i * 0.08 }}
                className="rounded-2xl border border-emerald-700/30 bg-emerald-900/30 backdrop-blur-sm p-6 sm:p-8 hover:border-gold/30 transition-colors">
                <span className="w-8 h-8 rounded-full bg-gold/20 text-gold text-sm font-bold flex items-center justify-center mb-4">{i + 1}</span>
                <h3 className="text-lg font-bold text-cream mb-2">{s.title}</h3>
                <p className="text-sm text-cream/70 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Good Fit / Not Fit */}
      <Section eyebrow="Fit Assessment" title="Is Your Property a Good Fit?">
        <div className="grid gap-8 lg:grid-cols-2">
          <Card className="!p-6 sm:!p-8">
            <h3 className="text-lg font-bold text-emerald-700 mb-4 flex items-center gap-2"><CheckCircle className="h-5 w-5" /> What Works</h3>
            <ul className="space-y-3">
              {GOOD_FIT.map((g, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#555]">
                  <CheckCircle className="h-4 w-4 text-emerald-500 mt-0.5 shrink-0" /> {g}
                </li>
              ))}
            </ul>
          </Card>
          <Card className="!p-6 sm:!p-8">
            <h3 className="text-lg font-bold text-red-600 mb-4 flex items-center gap-2"><XCircle className="h-5 w-5" /> What Does NOT Work</h3>
            <ul className="space-y-3">
              {NOT_FIT.map((n, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[#555]">
                  <XCircle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" /> {n}
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </Section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 sm:pb-28 lg:px-8">
        <motion.div {...fadeUp} className="relative rounded-2xl sm:rounded-[3rem] overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gold/40 via-emerald-500/25 to-gold/30 p-[2px] rounded-2xl sm:rounded-[3rem]">
            <div className="absolute inset-[2px] rounded-[calc(1rem-2px)] sm:rounded-[calc(3rem-2px)] bg-gradient-to-br from-[#03352a] via-[#022c22] to-[#044332]" />
          </div>
          <div className="relative p-6 sm:p-10 md:p-16 text-center">
            <h2 className="text-2xl font-semibold text-cream sm:text-3xl luxury-text-gradient">Have a Property?</h2>
            <p className="mt-4 max-w-xl mx-auto text-base text-cream-soft sm:text-lg">Submit your property for a no-obligation offer within 48 hours.</p>
            <div className="mt-8">
              <GoldButton onClick={() => navigateTo("sell")} className="!px-8 !py-4 !text-sm">
                Submit Your Property <ArrowRight className="h-4 w-4 ml-1 inline" />
              </GoldButton>
            </div>
          </div>
        </motion.div>
      </section>
    </>
  );
}
